import React, { useState } from 'react';
import { FontSizes, Heading, RadioList } from '@hexure/ui';
import Modal from 'shared/Modal';
import AlqApi from '../../utils/AlqApi';
import AppConfig from 'config/App';
import { UNDERWRITING_CLASS_ENUM, TABLE_RATING_ENUM } from 'constants/App';

const QUOTER_CONFIG = window.IXN_QUOTER_CONFIG || window.HEXURE_UQ_CONFIG || {};

const FirelightSimplifiedEticketModal = ({ onClose, quote, agent, client, ran_quote_data, appGuid }) => {
  const [isReplacement, setIsReplacement] = useState(false); // Tracks whether this is a replacement policy, defaults to false ("No") as ticket requirement
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState('validateViveUser');

  // Main API flow handler
  const handleCreateEticket = async () => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Validate User Registration
      setCurrentStep('validateViveUser');
      const agencyCode = agent?.agency_code || 'VS';
      const username = agent?.agent_user_id || 'baseAgent';

      if (!agencyCode || !username) {
        throw new Error('Agency code and username are required');
      }

      const validateResponse = await AlqApi.validateViveUser(agencyCode, username);

      if (validateResponse.error) {
        if (validateResponse.status === 400 || validateResponse.status === 500) {
          throw new Error('Simplified eTicket is not available at this time');
        }
        throw new Error(`User validation failed: ${validateResponse.error}`);
      }

      // Step 2: Register User if not already registered
      if (!validateResponse.is_user_registered) {
        setCurrentStep('registerViveUser');

        const registerParams = {
          agency_code: agencyCode,
          user_name: username,
          email_address: agent?.email || 'bestAgent@agency.com',
          first_name: agent?.first_name || 'Joe',
          last_name: agent?.last_name || 'Doe'
        };

        const registerResponse = await AlqApi.registerViveUser(registerParams);

        if (registerResponse.error) {
          if (registerResponse.status === 400 || registerResponse.status === 500) {
            throw new Error('Simplified eTicket is not available at this time');
          }
          throw new Error(`User registration failed: ${registerResponse.error}`);
        }
      }

      const quoteParams = {
        state: quote?.state || 'CA',
        gender: quote?.gender === 'Male' ? '1' : '2',
        date_of_birth: ran_quote_data?.date_of_birth ? new Date(ran_quote_data?.date_of_birth).toISOString().split('T')[0] : '',
        face_amount: parseFloat(quote?.face_amount || 0),
        product_id: quote?.product_id || '',
        term: Number.parseInt(quote?.product_type?.split(' ')[0], 10) || 0, // CS-144254 Defaulting to 0 if parsing fails
        is_smoker: quote?.tobacco || false,
        underwriting_class: UNDERWRITING_CLASS_ENUM[quote?.ixn_health_category] ?? UNDERWRITING_CLASS_ENUM[quote?.underwriting_class] ?? 0,
        // table_rating: String(TABLE_RATING_ENUM[quote?.ixn_health_category] ?? 0),
        purpose: quote?.purpose || 0,
        has_adb_rider: quote?.adb_rider || false,
        has_wop_rider: quote?.wop_rider || false,
        child_rider_units: quote?.child_rider_units || 0,
        agency_code: agencyCode,
        user_name: username,
        is_replacement: isReplacement,
        ...(QUOTER_CONFIG?.embed_context === 'fire_light' && {
          fl_domain: window.location.origin
        })
      };

      const quoteValidateResponse = await AlqApi.validateViveQuote(quoteParams);

      if (quoteValidateResponse.error) {
        if (quoteValidateResponse.status === 400 || quoteValidateResponse.status === 500) {
          throw new Error('Simplified eTicket is not available at this time');
        }
        throw new Error(`Quote validation failed: ${quoteValidateResponse.error}`);
      }

      // Step 4: Create Quote
      setCurrentStep('createViveQuote');

      // If appGuid is provided, pass it to createViveQuote
      let createQuoteParams = { ...quoteParams };
      if (appGuid) {
        createQuoteParams.appGuid = appGuid;
        createQuoteParams.external_system_id = appGuid;
      }

      const createQuoteResponse = await AlqApi.createViveQuote(createQuoteParams);

      if (createQuoteResponse.error) {
        if (createQuoteResponse.status === 400 || createQuoteResponse.status === 500) {
          throw new Error('Simplified eTicket is not available at this time');
        }
        throw new Error(`Quote creation failed: ${createQuoteResponse.error}`);
      }
      if (createQuoteResponse.quote_key && QUOTER_CONFIG.embed_context === 'fire_light') {
        // Update Eticket Data
        const data_items = [
          {
            id: 'FLI_QUOTE_KEY',
            Value: createQuoteResponse.quote_key
          },
          {
            id: 'FLI_AGENCY_CODE',
            Value: agencyCode
          }
        ];
        window.updateETicketData({ appId: appGuid, data_items });
      }
      if (createQuoteResponse.path) {
        // Step 5: Redirect to the path from response
        const baseUrl = AppConfig.VIVE_BASE_URL;
        const redirectUrl = `${baseUrl}${createQuoteResponse.path}`;

        // Redirect to the path
        window.open(redirectUrl, '_blank');

        // Close the modal
        onClose();
      } else {
        throw new Error('Quote creation completed but no redirect path was received from the response');
      }
    } catch (error) {
      setError(error.message || 'An error occurred during e-ticket creation');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '500px',
      margin: '0 auto',
      textAlign: 'center'
    },
    infoCard: {
      backgroundColor: '#f8f9fa',
      border: '1px solid #e9ecef',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px',
      textAlign: 'left'
    },
    infoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '8px',
      paddingBottom: '8px',
      borderBottom: '1px solid #e9ecef'
    },
    infoLabel: {
      fontWeight: '600',
      color: '#333'
    },
    infoValue: {
      color: '#666'
    },
    button: {
      padding: '12px 30px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600',
      marginRight: '10px',
      marginBottom: '10px'
    },
    buttonSecondary: {
      padding: '12px 30px',
      backgroundColor: '#6c757d',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600'
    },
    errorMessage: {
      color: '#dc3545',
      backgroundColor: '#f8d7da',
      border: '1px solid #f5c6cb',
      borderRadius: '6px',
      padding: '15px',
      marginBottom: '20px',
      textAlign: 'left'
    },
    loadingMessage: {
      color: '#007bff',
      backgroundColor: '#e7f1ff',
      border: '1px solid #b3d7ff',
      borderRadius: '6px',
      padding: '15px',
      marginBottom: '20px',
      textAlign: 'center'
    },
    stepIndicator: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '10px',
      fontStyle: 'italic'
    }
  };

  const stylesRadio = {
    pt10: {
      paddingTop: '10px'
    },
    radioflex: {
      display: 'flex',
      gap: '15px'
    }
  };

  const getStepLabel = step => {
    switch (step) {
      case 'validateViveUser':
        return 'Checking user registration status...';
      case 'registerViveUser':
        return 'Registering user with Vive system...';
      case 'validateViveQuote':
        return 'Validating quote parameters...';
      case 'createViveQuote':
        return 'Creating quote in Vive system...';
      default:
        return 'Processing...';
    }
  };

  return (
    <Modal onClose={onClose} width='600px'>
      <div style={styles.container}>
        <Heading level={2} style={{ fontSize: FontSizes.LARGE, marginBottom: '20px' }}>
          {appGuid ? 'eApply' : 'Create Simplified E-ticket'}
        </Heading>

        <div style={styles.infoCard}>
          <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>{appGuid ? 'E-Ticket Information' : 'Quote Details'}</h4>
          {appGuid && (
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Application GUID:</span>
              <span style={styles.infoValue}>{appGuid}</span>
            </div>
          )}
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Face Amount:</span>
            <span style={styles.infoValue}>${parseInt(quote?.face_amount || 0).toLocaleString()}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Product Type:</span>
            <span style={styles.infoValue}>{quote?.product_type}</span>
          </div>
          <div style={{ ...styles.infoRow, ...stylesRadio.pt10 }}>
            <span style={styles.infoLabel}>Is this a replacement?</span>
            <span style={{ ...styles.infoValue, ...stylesRadio.radioflex }}>
              <RadioList
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false }
                ]}
                name='is_replacement'
                value={isReplacement}
                onChange={() => {
                  setIsReplacement(!isReplacement); // Toggle the boolean value on change
                }}
              />
            </span>
          </div>
        </div>

        {error && (
          <div style={styles.errorMessage}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {loading && (
          <div style={styles.loadingMessage}>
            <div style={styles.stepIndicator}>{getStepLabel(currentStep)}</div>
            <div>Please wait while we process your e-ticket...</div>
          </div>
        )}

        {!loading && (
          <>
            <p style={{ marginBottom: '25px', color: '#666', lineHeight: '1.5' }}>
              Click "Create E-ticket" to automatically validate your agency, process the quote, and create the simplified e-ticket. You will be redirected to the application when complete.
            </p>
            <button style={styles.button} onClick={handleCreateEticket} disabled={loading}>
              Create E-ticket
            </button>
          </>
        )}

        <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e9ecef' }}>
          <button style={styles.buttonSecondary} onClick={onClose} disabled={loading}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default FirelightSimplifiedEticketModal;
